import { useMutation, UseMutationOptions } from "@tanstack/react-query";

interface IService {
  [key: string]: ActionKeyPair<unknown, unknown>;
}

type Action<T> = () => Promise<T>;
type ActionKeyPair<TParam, TValue> = {
  key: string;
  actionBuilder: (param: TParam) => Action<TValue>;
};

type ActionPairSelector<TService extends IService, TParam, TValue> = (
  service: TService
) => ActionKeyPair<TParam, TValue>;

type UseCustomMutationParams<TService extends IService, TParam, TValue> = {
  serviceInstance: TService;
  selectAction: ActionPairSelector<TService, TParam, TValue>;
} & Omit<UseMutationOptions<TValue, unknown, TParam>, "mutationFn">;

export function useCustomMutation<TService extends IService, TParam, TValue>({
  serviceInstance,
  selectAction,
  ...options
}: UseCustomMutationParams<TService, TParam, TValue>) {
  const { actionBuilder } = selectAction(serviceInstance);

  return useMutation({
    mutationFn: (param: TParam) => actionBuilder(param)(),
    ...options,
  });
}
